/**
 * @file `<Evolution/>` — structured "how this feature changed across
 *       eras" timeline, with `<Era/>` rungs and a `<Why/>` note.
 * @module @labcraft/course-kit/components/evolution
 *
 * The full "place this in time" surface of the history-of-evolution
 * tenet (`CONTENT_REBUILD_BRIEF.md` § "Teach The Evolution");
 * `<Aside variant="classic">` is the lightweight footnote instance.
 * Pure render — no extractor, scanner, or lab-state effect. All three
 * components are injected via `<MdxProvider/>`'s map, so chapters
 * write:
 *
 *   <Evolution of="package installation">
 *     <Era name="nix-env" when="2003–2021">
 *       `nix-env -iA` mutated a per-user profile in place.
 *     </Era>
 *     <Era name="flakes + nix profile" when="2021–" default>
 *       Inputs pinned in `flake.lock`; the profile is declarative.
 *     </Era>
 *     <Why>Mutable profiles made machines irreproducible; every rung
 *     moved the default toward pinned, declarative inputs.</Why>
 *   </Evolution>
 *
 * Prop-name decision (umbrella #1139 open decision #6, settled here):
 * `<Evolution of>` names the concept the timeline traces; `<Era name
 * when default>` names the era's tool/idiom (`name`), its
 * human-readable time range (`when`, free-form string — eras rarely
 * have crisp dates), and marks today's default recommendation
 * (`default`, boolean, at most one per timeline by authoring
 * convention); `<Why>` takes children only.
 */

import type { JSX, ReactNode } from 'react'
import { HistoryIcon } from './icons.tsx'
import styles from './evolution.module.css'

/**
 * Props for `<Evolution/>`.
 *
 * @property of The concept whose history the timeline traces (e.g.
 *           `"package installation"`); rendered as the block title.
 *           Optional — an untitled timeline renders label-only.
 * @property children `<Era/>` rungs in chronological order, then an
 *           optional `<Why/>` note.
 */
export interface EvolutionProps {
  of?: string
  children: ReactNode
}

/**
 * Timeline container: label chip, optional "How X evolved" title, and
 * a railed list of `<Era/>` rungs.
 *
 * @param props See {@link EvolutionProps}.
 * @returns The rendered timeline block.
 */
export function Evolution(props: EvolutionProps): JSX.Element {
  const title = props.of ? `How ${props.of} evolved` : 'Evolution'

  return (
    <section className={styles.evolution} aria-label={title}>
      <span className={styles.label}>
        <HistoryIcon />
        Evolution
      </span>
      {props.of && <h4 className={styles.title}>{title}</h4>}
      <div className={styles.timeline}>{props.children}</div>
    </section>
  )
}

/**
 * Props for `<Era/>`.
 *
 * @property name The era's tool or idiom (e.g. `"nix-env"`,
 *           `"flakes"`).
 * @property when Human-readable time range (free-form string, e.g.
 *           `"2003–2021"`, `"2021–"`) — eras rarely have crisp dates.
 * @property default Marks the era that is today's default
 *           recommendation; renders the "today's default" badge. At
 *           most one per timeline by authoring convention.
 * @property children What this era did and what it superseded.
 */
export interface EraProps {
  name: string
  when: string
  default?: boolean
  children: ReactNode
}

/**
 * One rung of the timeline: marker on the rail, name + range head,
 * body prose.
 *
 * @param props See {@link EraProps}.
 * @returns The rendered rung.
 */
export function Era(props: EraProps): JSX.Element {
  return (
    <div
      className={
        props.default ? `${styles.era} ${styles.eraDefault}` : styles.era
      }
      data-era={props.name}>
      <span className={styles.marker} aria-hidden />
      <div className={styles.eraHead}>
        <span className={styles.eraName}>{props.name}</span>
        <span className={styles.eraWhen}>{props.when}</span>
        {props.default && <span className={styles.badge}>today’s default</span>}
      </div>
      <div className={styles.eraBody}>{props.children}</div>
    </div>
  )
}

/**
 * Props for `<Why/>`.
 *
 * @property children The through-line: why the feature kept moving
 *           (what pressure each rung answered).
 */
export interface WhyProps {
  children: ReactNode
}

/**
 * The timeline's closing note — the reason the evolution happened,
 * rendered as a footer under the rungs.
 *
 * @param props See {@link WhyProps}.
 * @returns The rendered note.
 */
export function Why(props: WhyProps): JSX.Element {
  return (
    <div className={styles.why}>
      <span className={styles.whyLabel}>Why it moved</span>
      <div className={styles.whyBody}>{props.children}</div>
    </div>
  )
}
